import React from "react";
import { Collapse, Descriptions, Tag, Empty, Button } from "antd";
import { EditOutlined } from "@ant-design/icons";
import moment from "moment";
import { FixedIncomePaymentInstruction } from "../helpers/shared-interfaces";
import {
  formatSnakeCase,
  formatter,
  getStateColor,
} from "../helpers/utilities";

interface FixedIncomePaymentInstructionPanelProps {
  paymentInstruction?: FixedIncomePaymentInstruction;
  onEdit?: () => void;
}

export const FixedIncomePaymentInstructionPanel: React.FC<
  FixedIncomePaymentInstructionPanelProps
> = ({ paymentInstruction, onEdit }) => {
  if (!paymentInstruction) {
    return null;
  }

  const canEdit = paymentInstruction.state !== "verified";

  return (
    <Collapse style={{ marginTop: 16 }}>
      <Collapse.Panel
        header={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <span style={{ fontWeight: 500 }}>Payment Instruction</span>
            {canEdit && onEdit && (
              <Button
                className="base-button"
                icon={<EditOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                style={{ marginRight: 8 }}
              >
                Edit
              </Button>
            )}
          </div>
        }
        key="payment-instruction"
      >
        {paymentInstruction ? (
          <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }}>
            {paymentInstruction.rate && paymentInstruction.tenure && (
              <Descriptions.Item label={<b>Offer Details</b>}>
                {paymentInstruction.rate}% - {paymentInstruction.tenure} days
              </Descriptions.Item>
            )}

            {paymentInstruction.isin && (
              <Descriptions.Item label={<b>ISIN</b>}>
                {paymentInstruction.isin}
              </Descriptions.Item>
            )}

            {paymentInstruction.assignedTo && (
              <Descriptions.Item label={<b>Assigned To</b>}>
                {paymentInstruction.assignedTo.name}
              </Descriptions.Item>
            )}

            <Descriptions.Item label={<b>State</b>}>
              <Tag
                color={getStateColor(paymentInstruction.state || "")}
                style={{ textTransform: "capitalize" }}
              >
                {paymentInstruction.state
                  ? formatSnakeCase(paymentInstruction.state)
                  : "N/A"}
              </Tag>
            </Descriptions.Item>

            <Descriptions.Item label={<b>Total Amount Paid</b>}>
              <span>
                {paymentInstruction.totalAmountPaid
                  ? formatter.format(paymentInstruction.totalAmountPaid)
                  : "GHS 0.00"}
              </span>
            </Descriptions.Item>

            <Descriptions.Item label={<b>Created At</b>}>
              {moment(paymentInstruction.createdAt).format(
                "DD MMM YYYY, HH:mm",
              )}
            </Descriptions.Item>

            <Descriptions.Item label={<b>Last Updated</b>}>
              {moment(paymentInstruction.updatedAt).format(
                "DD MMM YYYY, HH:mm",
              )}
            </Descriptions.Item>

            {paymentInstruction.receiptUrl && (
              <Descriptions.Item label={<b>Payment Receipt</b>}>
                <a
                  href={paymentInstruction.receiptUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Receipt
                </a>
              </Descriptions.Item>
            )}

            {paymentInstruction.amountsPaid &&
              paymentInstruction.amountsPaid.length > 0 && (
                <Descriptions.Item label={<b>Payment Breakdown</b>} span={3}>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {paymentInstruction.amountsPaid.map((amount, index) => (
                      <Tag
                        key={index}
                        color="blue"
                        style={{ fontSize: "14px" }}
                      >
                        Payment {index + 1}: {formatter.format(amount)}
                      </Tag>
                    ))}
                  </div>
                </Descriptions.Item>
              )}
          </Descriptions>
        ) : (
          <Empty description="No payment instruction created yet" />
        )}
      </Collapse.Panel>
    </Collapse>
  );
};
